// Account screens in one modal: terms, sign in / sign up, forgot password, email verification,
// billing details, the account page, and the two prompts the platform needs (which progress to
// keep; "save your account first" before buying). Players start as guests and are only asked for
// an account when it matters ("play first, register to buy", as on The Archer Rouge).
import type { User } from 'firebase/auth';
import { EMAIL_RE, MIN_PASSWORD, displayNameOf, friendlyError, isVerifiedUser, type AccountInfo, type Accounts } from '../platform/auth';
import { LEGAL } from '../platform/config';
import { countries, isCountryCode } from '../platform/countries';

type View = 'terms' | 'signIn' | 'signUp' | 'forgot' | 'verify' | 'info' | 'ask' | 'gate';
/** Why the details form is open: straight after sign-up, before a first purchase, or from the account page. */
type InfoMode = 'complete' | 'billing' | 'manage';

export interface AccountHooks {
  /** A player just signed in (and verified): boot again so their cloud save is chosen and loaded. */
  signedIn(): void;
  /** Sign out: the caller uploads, sets this browser's save aside and restarts as a guest. */
  signOut(): Promise<void>;
}

const TERMS_KEY = 'tl.terms.accepted';

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

export class AccountUI {
  private root: HTMLElement;
  private body: HTMLElement;
  private view: View | null = null;
  private closable = true;
  private busy = false;
  private notNow = false;
  private infoMode: InfoMode = 'complete';
  private info: AccountInfo | null = null;
  /** Runs once billing details are saved (the purchase that asked for them). */
  private afterBilling: (() => void) | null = null;
  private resolveAsk: ((c: 'local' | 'cloud') => void) | null = null;
  private resolveTerms: (() => void) | null = null;

  constructor(private accounts: Accounts, private hooks: AccountHooks) {
    this.root = document.createElement('div');
    this.root.id = 'acct';
    this.root.hidden = true;
    this.root.innerHTML = `<div class="ov-card acct-card" role="dialog" aria-modal="true">
      <div class="ov-head"><h2 id="acct-title"></h2><button id="acct-close" class="icon-btn" aria-label="Close">✕</button></div>
      <div id="acct-body"></div></div>`;
    document.body.appendChild(this.root);
    this.body = this.root.querySelector('#acct-body')!;
    this.root.querySelector('#acct-close')!.addEventListener('click', () => this.close());
    this.root.addEventListener('click', (e) => {
      if (e.target === this.root) this.close();
      const eye = (e.target as HTMLElement).closest<HTMLButtonElement>('.fld-eye');
      if (eye) { togglePassword(eye); return; }
      const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-act]');
      if (btn && !btn.disabled) void this.act(btn.dataset.act!);
    });
    this.root.addEventListener('submit', (e) => { e.preventDefault(); void this.submit(e.target as HTMLFormElement); });
    this.root.addEventListener('change', (e) => {
      const t = e.target as HTMLInputElement;
      if (t.name === 'agree') this.body.querySelectorAll<HTMLButtonElement>('[data-needs-agree]').forEach((b) => { b.disabled = !t.checked; });
    });
    window.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.view) this.close(); });
  }

  // ---- Entry points ---------------------------------------------------------------------------

  termsAccepted(): boolean { try { return localStorage.getItem(TERMS_KEY) === '1'; } catch { return true; } }

  /** First run: the terms, which can't be dismissed. Resolves once accepted. */
  requireTerms(): Promise<void> {
    if (this.termsAccepted()) return Promise.resolve();
    return new Promise((resolve) => { this.resolveTerms = resolve; this.show('terms', false); });
  }

  /** From the game ("Save your progress"): sign in, with a way out. */
  openSaveAccount(): void { this.notNow = true; this.show('signIn'); }

  /** The 👤 button: the account page when signed in, else sign in. */
  async openAccount(): Promise<void> {
    const u = this.accounts.user;
    if (!u) return this.openSaveAccount();
    if (!isVerifiedUser(u)) return this.show('verify');
    await this.openInfo('manage');
  }

  /** Buying needs an account; guests (and unverified players) are sent here first. */
  gate(): void {
    const u = this.accounts.user;
    if (u && !isVerifiedUser(u)) this.show('verify');
    else this.show('gate');
  }

  /** Makes sure checkout has a name and a country, then runs `then`. */
  async requireBilling(then: () => void): Promise<void> {
    const info = await this.accounts.loadAccountInfo();
    if (info.firstName && info.lastName && isCountryCode(info.country)) return then();
    this.afterBilling = then;
    await this.openInfo('billing', info);
  }

  /** Guest progress meets an account with progress: the player picks which to keep. */
  askWhichSave(localStage: number, cloudLevel: number): Promise<'local' | 'cloud'> {
    return new Promise((resolve) => {
      this.resolveAsk = resolve;
      this.askText = { localStage, cloudLevel };
      this.show('ask', false);
    });
  }
  private askText = { localStage: 1, cloudLevel: 1 };

  close(): void {
    if (!this.closable || !this.view) return;
    this.view = null;
    this.root.hidden = true;
    this.afterBilling = null;
    this.notNow = false;
  }

  // ---- Rendering ------------------------------------------------------------------------------

  private show(view: View, closable = true): void {
    this.view = view;
    this.closable = closable;
    this.busy = false;
    const [title, html] = this.render(view);
    (this.root.querySelector('#acct-title') as HTMLElement).textContent = title;
    (this.root.querySelector('#acct-close') as HTMLElement).hidden = !closable;
    this.body.innerHTML = (HERO_VIEWS.has(view) ? HERO : '') + html + (PERKS_VIEWS.has(view) ? PERKS : '') + '<p class="acct-msg" role="status"></p>';
    dressFields(this.body);
    this.root.hidden = false;
    this.body.querySelector<HTMLInputElement>('input:not([type=checkbox])')?.focus();
  }

  private async openInfo(mode: InfoMode, known?: AccountInfo): Promise<void> {
    this.infoMode = mode;
    this.info = known ?? await this.accounts.loadAccountInfo();
    if (mode === 'complete' && !this.info.firstName && this.accounts.user) {
      const [first, ...rest] = displayNameOf(this.accounts.user).split(' ');
      this.info = { ...this.info, firstName: first ?? '', lastName: rest.join(' ') };
    }
    this.show('info', mode !== 'complete');
  }

  private render(view: View): [string, string] {
    const agree = `<label class="acct-check"><input type="checkbox" name="agree"> I agree to the
      <a href="${LEGAL.terms}" target="_blank" rel="noopener">Terms of Service</a> and
      <a href="${LEGAL.privacy}" target="_blank" rel="noopener">Privacy Policy</a></label>`;
    const google = `<div class="acct-or"><span>or</span></div>
      <button type="button" class="acct-google" data-act="google">${GOOGLE_G}<span>Continue with Google</span></button>`;
    switch (view) {
      case 'terms':
        return ['Welcome to Tiny Legends', `<p class="ov-sub">Play free right away as a guest. Make an account any time to
          keep your progress on every device and to buy Gems.</p>${agree}
          <button class="acct-btn" data-act="accept-terms" data-needs-agree disabled>Let's play!</button>`];
      case 'signIn':
        return ['Save your progress', `<p class="ov-sub">Sign in to keep your toys, Treasures and Gems safe on every device.</p>
          <form data-form="signIn">
            <input name="email" type="email" autocomplete="email" placeholder="Email" required>
            <input name="password" type="password" autocomplete="current-password" placeholder="Password" required>
            <label class="acct-check"><input type="checkbox" name="remember" checked> Keep me signed in</label>
            <button class="acct-btn">Sign in</button>
          </form>${google}
          <div class="acct-links"><button class="link" data-act="to-signUp">Create an account</button>
            <button class="link" data-act="to-forgot">Forgot password?</button></div>
          ${this.notNow ? '<button class="link acct-notnow" data-act="close">Not now</button>' : ''}`];
      case 'signUp':
        return ['Create your account', `<form data-form="signUp">
            <input name="name" autocomplete="name" placeholder="Full name" required>
            <input name="email" type="email" autocomplete="email" placeholder="Email" required>
            <input name="password" type="password" autocomplete="new-password" placeholder="Password (at least ${MIN_PASSWORD} characters)" required>
            <input name="confirm" type="password" autocomplete="new-password" placeholder="Confirm password" required>
            ${agree}
            <button class="acct-btn" data-needs-agree disabled>Create account</button>
          </form>${google}
          <div class="acct-links"><button class="link" data-act="to-signIn">I already have an account</button></div>`];
      case 'forgot':
        return ['Reset your password', `<p class="ov-sub">We'll email you a link to choose a new password.</p>
          <form data-form="forgot"><input name="email" type="email" autocomplete="email" placeholder="Email" required>
            <button class="acct-btn">Send reset link</button></form>
          <div class="acct-links"><button class="link" data-act="to-signIn">Back to sign in</button></div>`];
      case 'verify': {
        const email = esc(this.accounts.user?.email ?? 'your email');
        return ['Check your email', `<p class="ov-sub">We sent a link to <b>${email}</b>. Open it to confirm your account, then come back here.</p>
          <button class="acct-btn" data-act="verified">I've verified</button>
          <button class="acct-btn alt" data-act="resend">Resend the email</button>
          <div class="acct-links"><button class="link" data-act="other-account">Use a different account</button></div>`];
      }
      case 'info': {
        const i = this.info ?? { firstName: '', lastName: '', address: '', phone: '', country: '', zipCode: '' };
        const opts = countries().map((c) => `<option value="${c.code}" ${c.code === i.country ? 'selected' : ''}>${esc(c.name)}</option>`).join('');
        const field = (name: keyof AccountInfo, label: string, auto: string, required = false) =>
          `<input name="${name}" autocomplete="${auto}" placeholder="${label}${required ? '' : ' (optional)'}" value="${esc(i[name])}" ${required ? 'required' : ''}>`;
        const titles: Record<InfoMode, string> = { complete: "Let's finish your account", billing: 'Billing details', manage: 'Your account' };
        const sub: Record<InfoMode, string> = {
          complete: 'These go on your receipts when you buy Gems.',
          billing: 'The payment page needs these once. They go on your receipts.',
          manage: `Signed in as <b>${esc(this.accounts.user?.email ?? '')}</b>`,
        };
        return [titles[this.infoMode], `<p class="ov-sub">${sub[this.infoMode]}</p>
          <form data-form="info">
            <div class="acct-row">${field('firstName', 'First name', 'given-name', true)}${field('lastName', 'Last name', 'family-name', true)}</div>
            <select name="country" required><option value="">Country</option>${opts}</select>
            ${field('address', 'Street address', 'street-address')}
            <div class="acct-row">${field('zipCode', 'Postcode', 'postal-code')}${field('phone', 'Phone', 'tel')}</div>
            <button class="acct-btn">${this.infoMode === 'manage' ? 'Save changes' : this.infoMode === 'billing' ? 'Continue to payment' : 'Save'}</button>
          </form>
          ${this.infoMode === 'manage' ? `<div class="acct-links"><button class="link" data-act="change-password">Change password</button>
            <button class="link danger" data-act="sign-out">Sign out</button></div>` : ''}`];
      }
      case 'ask':
        return ['Two lots of progress', `<p class="ov-sub">This device has progress you made as a guest, and your account has its own.
          Which one do you want to keep playing? The other is kept safely on this device.</p>
          <button class="acct-btn" data-act="keep-local">Keep this device's (best stage ${this.askText.localStage})</button>
          <button class="acct-btn alt" data-act="keep-cloud">Keep my account's${this.askText.cloudLevel > 1 ? ` (best stage ${this.askText.cloudLevel})` : ''}</button>`];
      case 'gate':
        return ['Save your account first', `<p class="ov-sub">Gems are bought with a Tiny Legends account, so your purchase is
          safe on every device and never lost.</p>
          <button class="acct-btn" data-act="to-signIn-notnow">Save my account</button>
          <button class="link acct-notnow" data-act="close">Not now</button>`];
    }
  }

  private message(text: string, ok = false): void {
    const el = this.body.querySelector('.acct-msg') as HTMLElement | null;
    if (!el) return;
    el.textContent = text;
    el.classList.toggle('ok', ok);
  }

  /** Runs an async step with the buttons locked and errors shown in the dialog. */
  private async run(step: () => Promise<void>): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    const buttons = [...this.body.querySelectorAll<HTMLButtonElement>('button')];
    const was = buttons.map((b) => b.disabled);
    buttons.forEach((b) => { b.disabled = true; });
    this.message('');
    try {
      await step();
    } catch (e) {
      this.message(friendlyError(e));
    } finally {
      this.busy = false;
      buttons.forEach((b, i) => { if (b.isConnected) b.disabled = was[i]!; });
    }
  }

  // ---- Actions --------------------------------------------------------------------------------

  private async act(a: string): Promise<void> {
    switch (a) {
      case 'close': this.close(); break;
      case 'accept-terms':
        try { localStorage.setItem(TERMS_KEY, '1'); } catch { /* storage blocked: asked again next visit */ }
        this.closable = true; this.close();
        this.resolveTerms?.(); this.resolveTerms = null;
        break;
      case 'to-signIn': this.show('signIn'); break;
      case 'to-signIn-notnow': this.notNow = true; this.show('signIn'); break;
      case 'to-signUp': this.show('signUp'); break;
      case 'to-forgot': this.show('forgot'); break;
      case 'google': await this.run(async () => this.afterAuth(await this.accounts.signInWithGoogle(), false)); break;
      case 'verified':
        await this.run(async () => {
          if (!(await this.accounts.checkVerified())) { this.message("Not verified yet. Open the link in the email, then try again."); return; }
          await this.openInfo('complete');
        });
        break;
      case 'resend':
        await this.run(async () => { await this.accounts.sendVerification(); this.message('Sent! Check your inbox (and spam folder).', true); });
        break;
      case 'other-account': await this.run(async () => { await this.accounts.signOut(); this.show('signIn'); }); break;
      case 'change-password':
        await this.run(async () => {
          await this.accounts.forgotPassword(this.accounts.user?.email ?? '');
          this.message('We emailed you a link to choose a new password.', true);
        });
        break;
      case 'sign-out': await this.run(async () => { this.closable = true; this.close(); await this.hooks.signOut(); }); break;
      case 'keep-local': case 'keep-cloud':
        this.closable = true; this.close();
        this.resolveAsk?.(a === 'keep-local' ? 'local' : 'cloud'); this.resolveAsk = null;
        break;
    }
  }

  private async submit(form: HTMLFormElement): Promise<void> {
    const f = new FormData(form);
    const val = (k: string) => String(f.get(k) ?? '').trim();
    switch (form.dataset.form) {
      case 'signIn':
        if (!EMAIL_RE.test(val('email'))) return this.message("That email address doesn't look right.");
        return this.run(async () => this.afterAuth(await this.accounts.signIn(val('email'), String(f.get('password') ?? ''), f.get('remember') === 'on'), false));
      case 'signUp': {
        const pw = String(f.get('password') ?? '');
        if (!val('name')) return this.message('Please enter your name.');
        if (!EMAIL_RE.test(val('email'))) return this.message("That email address doesn't look right.");
        if (pw.length < MIN_PASSWORD) return this.message(`Use at least ${MIN_PASSWORD} characters for your password.`);
        if (pw !== String(f.get('confirm') ?? '')) return this.message("The passwords don't match.");
        return this.run(async () => this.afterAuth(await this.accounts.signUp(val('name'), val('email'), pw), true));
      }
      case 'forgot':
        if (!EMAIL_RE.test(val('email'))) return this.message("That email address doesn't look right.");
        return this.run(async () => {
          await this.accounts.forgotPassword(val('email'));
          this.message('If that email has an account, a reset link is on its way.', true);
        });
      case 'info': {
        const info: AccountInfo = {
          firstName: val('firstName'), lastName: val('lastName'), address: val('address'),
          phone: val('phone'), country: val('country'), zipCode: val('zipCode'),
        };
        if (!info.firstName || !info.lastName) return this.message('Please enter your first and last name.');
        if (!isCountryCode(info.country)) return this.message('Please choose your country.');
        return this.run(async () => {
          await this.accounts.saveAccountInfo(info);
          const mode = this.infoMode, then = this.afterBilling;
          this.afterBilling = null;
          if (mode === 'manage') { this.message('Saved.', true); return; }
          this.closable = true; this.close();
          if (mode === 'complete') this.hooks.signedIn();
          else then?.();
        });
      }
    }
  }

  /** Where a successful sign-in / sign-up goes next. */
  private afterAuth(user: User, signedUp: boolean): void {
    if (!isVerifiedUser(user)) { this.show('verify'); return; }
    if (signedUp) { void this.openInfo('complete'); return; }
    this.closable = true; this.close();
    this.hooks.signedIn();
  }
}

// ---- Look ------------------------------------------------------------------------------------

const svg = (d: string) => `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="${d}"/></svg>`;
/** Field icons, by input name (Material-style outlines). */
const FIELD_ICONS: Record<string, string> = {
  email: svg('M4 5h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2zm0 2v.4l8 5 8-5V7H4zm16 2.7-8 5-8-5V17h16V9.7z'),
  password: svg('M12 2a5 5 0 0 1 5 5v3h1a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h1V7a5 5 0 0 1 5-5zm0 2a3 3 0 0 0-3 3v3h6V7a3 3 0 0 0-3-3zm0 9a2 2 0 0 1 1 3.7V19h-2v-2.3A2 2 0 0 1 12 13z'),
  name: svg('M12 12a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm0-2a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm-8 12a8 8 0 0 1 16 0h-2a6 6 0 0 0-12 0H4z'),
  phone: svg('M6.6 10.8a15 15 0 0 0 6.6 6.6l2.2-2.2a1 1 0 0 1 1-.25 11.4 11.4 0 0 0 3.6.57 1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.57a1 1 0 0 1-.25 1L6.6 10.8z'),
  address: svg('M12 2a7 7 0 0 1 7 7c0 5.25-7 13-7 13S5 14.25 5 9a7 7 0 0 1 7-7zm0 4.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5z'),
  country: svg('M12 2a10 10 0 1 1 0 20 10 10 0 0 1 0-20zm6.9 6a8 8 0 0 0-3.5-3.3c.6 1 1 2.1 1.3 3.3h2.2zM12 4c-.8 1.2-1.4 2.6-1.8 4h3.6c-.4-1.4-1-2.8-1.8-4zM4.3 14h3.3a16 16 0 0 1 0-4H4.3a8 8 0 0 0 0 4zm.8 2a8 8 0 0 0 3.5 3.3c-.6-1-1-2.1-1.3-3.3H5.1zm2.2-8c.3-1.2.7-2.3 1.3-3.3A8 8 0 0 0 5.1 8h2.2zM12 20c.8-1.2 1.4-2.6 1.8-4h-3.6c.4 1.4 1 2.8 1.8 4zm2.3-6a14 14 0 0 0 0-4H9.7a14 14 0 0 0 0 4h4.6zm1.1 5.3A8 8 0 0 0 18.9 16h-2.2c-.3 1.2-.7 2.3-1.3 3.3zM16.4 14h3.3a8 8 0 0 0 0-4h-3.3a16 16 0 0 1 0 4z'),
};
FIELD_ICONS.confirm = FIELD_ICONS.password!;
FIELD_ICONS.firstName = FIELD_ICONS.lastName = FIELD_ICONS.name!;
FIELD_ICONS.zipCode = FIELD_ICONS.address!;
const EYE = svg('M12 5c5 0 9 4.5 10 7-1 2.5-5 7-10 7S3 14.5 2 12c1-2.5 5-7 10-7zm0 2c-3.6 0-6.6 3-7.8 5 1.2 2 4.2 5 7.8 5s6.6-3 7.8-5c-1.2-2-4.2-5-7.8-5zm0 2a3 3 0 1 1 0 6 3 3 0 0 1 0-6z');
const GOOGLE_G = `<svg viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.4 30.2 0 24 0 14.6 0 6.6 5.4 2.6 13.2l7.8 6.1C12.3 13.6 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4 7.1-10 7.1-17.5z"/><path fill="#FBBC05" d="M10.4 28.7A14.5 14.5 0 0 1 9.5 24c0-1.6.3-3.2.8-4.7l-7.8-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.8l7.8-6.1z"/><path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.5-5.8c-2.1 1.4-4.8 2.3-8.4 2.3-6.3 0-11.7-4.1-13.6-9.8l-7.8 6.1C6.6 42.6 14.6 48 24 48z"/></svg>`;
const HERO = '<div class="acct-hero"><img src="art/logo_main.png" alt="Tiny Legends"></div>';
const PERKS = `<ul class="acct-perks">
  <li><i class="ic ic-gem"></i>Gems you buy are kept safe</li>
  <li><i>☁️</i>Progress saved on every device</li>
  <li><i>🛡️</i>Free to play, no ads, ever</li></ul>`;
const HERO_VIEWS: ReadonlySet<View> = new Set(['terms', 'signIn', 'signUp', 'gate']);
const PERKS_VIEWS: ReadonlySet<View> = new Set(['signIn', 'gate']);

/** Wraps each text field in an icon frame (and gives passwords a show/hide eye). */
function dressFields(root: HTMLElement): void {
  root.querySelectorAll<HTMLInputElement | HTMLSelectElement>('input:not([type=checkbox]), select').forEach((el) => {
    const wrap = document.createElement('div');
    wrap.className = 'fld';
    wrap.innerHTML = `<span class="fld-ic">${FIELD_ICONS[el.name] ?? ''}</span>`;
    el.replaceWith(wrap);
    wrap.append(el);
    if (el instanceof HTMLInputElement && el.type === 'password') {
      wrap.insertAdjacentHTML('beforeend', `<button type="button" class="fld-eye" aria-label="Show password">${EYE}</button>`);
    }
  });
}

function togglePassword(eye: HTMLButtonElement): void {
  const input = eye.parentElement?.querySelector('input');
  if (!input) return;
  const show = input.type === 'password';
  input.type = show ? 'text' : 'password';
  eye.classList.toggle('on', show);
  eye.setAttribute('aria-label', show ? 'Hide password' : 'Show password');
}
